import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, App as AntdApp } from 'antd';
import './style.css';
import { supabase } from '../../supabaseClient';

const Dashboard: React.FC = () => {
  const [userStats, setUserStats] = useState({
    total: 0,
    normal: 0,
    pow: 0,
    pos: 0,
    today: 0,
  });
  const [deviceStats, setDeviceStats] = useState({ total: 0, wait: 0, success: 0, fail: 0 });
  const [assetStats, setAssetStats] = useState({
    total: 0,
    frozen: 0,
    available: 0,
    locked: 0,
    totalRelease: 0,
    yesterdayRelease: 0,
  });
  const [nodeStats, setNodeStats] = useState({
    total: 0,
    locked: 0,
  });
  const [chainStats, setChainStats] = useState({ deposit: 0, withdraw: 0 });
  const [withdrawStats, setWithdrawStats] = useState({ pending: 0, success: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      // 用户统计
      const { data: allUsers } = await supabase.from('u_profiles').select('user_type,created_at');
      // 资产统计
      const { data: allAssets } = await supabase.from('u_user_assets').select('total_amount,frozen_amount,available_amount,locked_amount');
      // 释放算力统计
      // 需要把status字段也查出来
      const { data: allRelease } = await supabase.from('u_device_duties').select('amount,date,status');
      // 节点统计
      const { data: allNodes } = await supabase.from('u_user_nodes').select('total_amount,locked_amount');
      let assetTotal = 0, assetFrozen = 0, assetAvailable = 0, assetLocked = 0;
      let totalRelease = 0, yesterdayRelease = 0;
      let nodeTotal = 0, nodeLocked = 0;
      if (allAssets) {
        allAssets.forEach(a => {
          assetTotal += Number(a.total_amount || 0);
          assetFrozen += Number(a.frozen_amount || 0);
          assetAvailable += Number(a.available_amount || 0);
          assetLocked += Number(a.locked_amount || 0);
        });
      }
      if (allRelease) {
        const now = new Date();
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const yesterdayStr = yesterday.toISOString().slice(0, 10);
        allRelease.forEach(r => {
          // 只统计已结算且amount大于0（status === 'done' 且 amount > 0）
          const isSettled = r.status === 'done' && Number(r.amount) > 0;
          if (isSettled) {
            totalRelease += Number(r.amount || 0);
            if (r.date && r.date.startsWith(yesterdayStr)) yesterdayRelease += Number(r.amount || 0);
          }
        });
      }
      if (allNodes) {
        allNodes.forEach(n => {
          nodeTotal += Number(n.total_amount || 0);
          nodeLocked += Number(n.locked_amount || 0);
        });
      }
      setAssetStats({
        total: assetTotal,
        frozen: assetFrozen,
        available: assetAvailable,
        locked: assetLocked,
        totalRelease,
        yesterdayRelease,
      });
      setNodeStats({ total: nodeTotal, locked: nodeLocked });
      if (!allUsers) return;
      const now = new Date();
      const todayStr = now.toISOString().slice(0, 10);
      let total = allUsers.length;
      let normal = 0, pow = 0, pos = 0, today = 0;
      allUsers.forEach(u => {
        if (u.user_type === 0) normal++;
        if (u.user_type === 1) pow++;
        if (u.user_type === 2) pos++;
        if (u.created_at && u.created_at.startsWith(todayStr)) today++;
      });
      setUserStats({ total, normal, pow, pos, today });
    };
    fetchStats();

    // 设备统计
    (async () => {
      const { data: devices } = await supabase.from('u_user_device').select('status');
      let total = 0, wait = 0, success = 0, fail = 0;
      (devices || []).forEach((d: any) => {
        total++;
        if (d.status === 0 || d.status === '0') wait++;
        else if (d.status === 1 || d.status === '1') success++;
        else if (d.status === 2 || d.status === '2') fail++;
      });
      setDeviceStats({ total, wait, success, fail });
    })();

    // 链上统计
    (async () => {
      const { data: depositList } = await supabase.from('u_transactions').select('value').eq('type', 'deposit').eq('token_symbol', 'USDT').eq('status', 'success');
      const { data: withdrawList } = await supabase.from('u_transactions').select('value').eq('type', 'withdraw').eq('token_symbol', 'UCDN').eq('status', 'success');
      let deposit = 0, withdraw = 0;
      if (depositList) depositList.forEach((d: any) => { deposit += Number(d.value || 0); });
      if (withdrawList) withdrawList.forEach((w: any) => { withdraw += Number(w.value || 0); });
      setChainStats({ deposit, withdraw });
    })();

    // 提取统计
    (async () => {
      const { data: withdraws } = await supabase.from('u_withdraws').select('status,amount');
      let pending = 0, success = 0;
      (withdraws || []).forEach((w: any) => {
        if (w.status === '0' || w.status === 0) pending += Number(w.amount || 0);
        else if (w.status === '1' || w.status === 1) success += Number(w.amount || 0);
      });
      setWithdrawStats({ pending, success });
    })();
  }, []);

  const stats = [
    { group: '用户', title: '总用户', value: userStats.total, color: '#f5c16c' },
    { group: '用户', title: '普通用户', value: userStats.normal, color: '#ffb86b' },
    { group: '用户', title: '大节点 POW', value: userStats.pow, color: '#ffd6a0' },
    { group: '用户', title: '小节点 POS', value: userStats.pos, color: '#ffe1b2' },
    { group: '用户', title: '今日注册', value: userStats.today, color: '#ffe1b2' },
    { group: '设备', title: '设备总数', value: deviceStats.total, color: '#b6e3f5' },
    { group: '设备', title: '等待绑定', value: deviceStats.wait, color: '#e6e6e6' },
    { group: '设备', title: '绑定成功', value: deviceStats.success, color: '#b7e4c7' },
    { group: '设备', title: '绑定失败', value: deviceStats.fail, color: '#f5b6b6' },
  ];

  const groupOrder = ['用户', '设备'];

  return (
    <AntdApp>
      <div className="dashboard-stats">
        <h2 style={{fontWeight:800, fontSize:26, color:'#b06a19', marginBottom:32, letterSpacing:2}}>运营总览</h2>
        {groupOrder.map(group => (
          <div key={group} style={{ marginBottom: 32 }}>
            <div style={{ fontWeight: 700, fontSize: 20, color: '#b06a19', marginBottom: 16 }}>{group}</div>
            <Row gutter={[24, 24]} justify="start">
              {stats.filter(item => item.group === group).map(item => (
                <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.title} style={{ marginBottom: 0 }}>
                  <Card variant="outlined" style={{ borderRadius: 16, background: item.color, boxShadow: '0 2px 12px #f5c16c22', minHeight: 120 }}>
                    <Statistic
                      title={<span style={{ fontWeight: 700, fontSize: 18 }}>{item.title}</span>}
                      value={item.value}
                      valueStyle={{ color: '#4b2e13', fontWeight: 800, fontSize: 32 }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ))}
        {/* 资产统计卡片 */}
        <div style={{ fontWeight: 700, fontSize: 20, color: '#b06a19', marginBottom: 16 }}>资产</div>
        <Row gutter={[24, 24]} justify="start">
          {[{
            label: '总额',
            value: assetStats.total,
            color: '#f5c16c',
          }, {
            label: '可用',
            value: assetStats.available,
            color: '#b7e4c7',
          }, {
            label: '锁定',
            value: assetStats.locked,
            color: '#f5b6b6',
          }].map((item) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.label} style={{ marginBottom: 0 }}>
              <Card variant="outlined" style={{ borderRadius: 16, background: item.color, boxShadow: '0 2px 12px #f5c16c22', minHeight: 120 }}>
                <Statistic
                  title={<span style={{ fontWeight: 700, fontSize: 18 }}>{item.label}</span>}
                  value={Math.round(Number(item.value)).toLocaleString()}
                  valueStyle={{ color: '#4b2e13', fontWeight: 800, fontSize: 32 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
        {/* 数据统计卡片 */}
        <div style={{ fontWeight: 700, fontSize: 20, color: '#b06a19', margin: '32px 0 16px 0' }}>数据</div>
        <Row gutter={[24, 24]} justify="start" style={{ marginBottom: 24 }}>
          {[
            {
              label: '总产出算力',
              value: assetStats.totalRelease,
              color: '#e0e7ff',
            },
            {
              label: '昨日产出算力',
              value: assetStats.yesterdayRelease,
              color: '#ffe0e0',
            },
          ].map((item) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.label} style={{ marginBottom: 0 }}>
              <Card variant="outlined" style={{ borderRadius: 16, background: item.color, boxShadow: '0 2px 12px #f5c16c22', minHeight: 120 }}>
                <Statistic
                  title={<span style={{ fontWeight: 700, fontSize: 18 }}>{item.label}</span>}
                  value={Math.round(Number(item.value)).toLocaleString()}
                  valueStyle={{ color: '#4b2e13', fontWeight: 800, fontSize: 32 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
        <Row gutter={[24, 24]} justify="start" style={{ marginBottom: 32 }}>
          {[
            {
              label: '节点历史总额',
              value: nodeStats.total,
              color: '#e0ffe7',
            },
            {
              label: '节点当前锁定',
              value: nodeStats.locked,
              color: '#e0f7ff',
            },
          ].map((item) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.label} style={{ marginBottom: 0 }}>
              <Card variant="outlined" style={{ borderRadius: 16, background: item.color, boxShadow: '0 2px 12px #f5c16c22', minHeight: 120 }}>
                <Statistic
                  title={<span style={{ fontWeight: 700, fontSize: 18 }}>{item.label}</span>}
                  value={Math.round(Number(item.value)).toLocaleString()}
                  valueStyle={{ color: '#4b2e13', fontWeight: 800, fontSize: 32 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
        {/* 新增：提取统计 */}
        <Row gutter={[24, 24]} justify="start" style={{ marginBottom: 32 }}>
          {[
            { label: '提取中', value: Math.round(withdrawStats.pending), color: '#ffe1b2' },
            { label: '提取成功', value: Math.round(withdrawStats.success), color: '#b7e4c7' },
          ].map((item) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.label} style={{ marginBottom: 0 }}>
              <Card variant="outlined" style={{ borderRadius: 16, background: item.color, boxShadow: '0 2px 12px #f5c16c22', minHeight: 120 }}>
                <Statistic
                  title={<span style={{ fontWeight: 700, fontSize: 18 }}>{item.label}</span>}
                  value={item.value.toLocaleString()}
                  valueStyle={{ color: '#4b2e13', fontWeight: 800, fontSize: 32 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
        {/* 链上统计卡片 */}
        <div style={{ fontWeight: 700, fontSize: 20, color: '#b06a19', margin: '32px 0 16px 0' }}>链上</div>
        <Row gutter={[24, 24]} justify="start" style={{ marginBottom: 32 }}>
          {[
            {
              label: '充油总额',
              value: chainStats.deposit,
              color: '#e0e7ff',
            },
            {
              label: '提取总额(UCDN)',
              value: chainStats.withdraw,
              color: '#ffe0e0',
            },
          ].map((item) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.label} style={{ marginBottom: 0 }}>
              <Card variant="outlined" style={{ borderRadius: 16, background: item.color, boxShadow: '0 2px 12px #f5c16c22', minHeight: 120 }}>
                <Statistic
                  title={<span style={{ fontWeight: 700, fontSize: 18 }}>{item.label}</span>}
                  value={Math.round(Number(item.value)).toLocaleString()}
                  valueStyle={{ color: '#4b2e13', fontWeight: 800, fontSize: 32 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </AntdApp>
  );
};

export default Dashboard;
